import { ReactElement, useEffect, useMemo, useRef } from 'react'

import AddAccountButton from 'App/AddAccountModal/AddAccountButton'
import Loader from 'authorization-module/lib/ui/atoms/Loader'
import queryClient from 'const/queryClient'
import {
  useAllAccountSummary,
  useAutoRegisterUpdate,
  useCreditAccounts
} from 'const/syfApiQueries'
import getAutoRegisterPayload from 'helpers/getAutoRegisterPayload'
import triggerAnalyticsEvent, {
  setAnalyticsDefaults
} from 'helpers/triggerAnalyticsEvent'
import WelcomeInterstitial from 'pages/AccountsDashboard/WelcomeInterstitial'
import i18n from 'strings/i18n'
import { creditAccountsCacheKey } from '@syf-api-library/credit-accounts'
import PersonalGreeting from 'ui/patterns/PersonalGreeting'

import useNotificationCenter from 'hooks/useNotificationCenter'
import NotificationCenter from 'ui/atoms/NotificationCenter'
import { useAtom, useAtomValue } from 'jotai'
import {
  dashboardAnalyticsAtom,
  featureFlagsFetchedAtom,
  modalStatusAtom
} from 'store/atoms'
import shouldAccountDashboardSkipAnalytics from 'helpers/shouldAccountDashboardSkipAnalytics'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import { getBoolFromApptimize } from 'helpers/getBoolFromApptimize'
import {
  AccountManagerContainer,
  AccountManagerGrid,
  AccountManagerText,
  AccountsContainer,
  DashboardAccountGrid,
  DashboardContainer,
  ErrorContainer,
  MainContainer,
  PageWrapper
} from './subcomponents'
import TechnicalError from './TechnicalError'
import AccountCard from './Card/AccountCard'
import DynamicYieldGrid from './DynamicYieldGrid'
import MarketingWrapper from './MarketingMFE'

const technicalDifficultyMessage =
  'We are currently experiencing technical difficulties and are unable to access your account. Please try again later or contact customer service for assistance.'

const AccountsDashboard = (): ReactElement => {
  // To restrict from running in loop wrt creditaccount in case of any API issues
  const autoregisterCounter = useRef(0)
  const lastAnalyticsAccountCountRef = useRef<number>()
  const { isLoading: isAutoRegisterLoading, mutate: mutateAutoRegister } =
    useAutoRegisterUpdate()

  const {
    data: { accounts = [], electronicConsent, syfId } = {},
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
    error: accountError,
    isError: isCreditAccountsAPIError
  } = useCreditAccounts({
    onSuccess: (data) => {
      if (data.electronicConsent) {
        const accountsToAutoRegister = getAutoRegisterPayload(data.accounts)

        if (accountsToAutoRegister.length && autoregisterCounter.current < 1) {
          mutateAutoRegister(accountsToAutoRegister, {
            onSuccess: () => {
              queryClient.invalidateQueries(creditAccountsCacheKey())
              autoregisterCounter.current += 1
            }
          })
        }
      }
    }
  })
  const { shouldShowNotificationCenter } = useNotificationCenter()

  const numberOfAccounts = accounts?.length
  const isAccountEmpty = !numberOfAccounts

  const renderWelcomeInterstitial =
    !isAccountsLoading &&
    !electronicConsent &&
    !isAutoRegisterLoading &&
    !accountError &&
    !isAccountEmpty

  const [{ modalId }] = useAtom(modalStatusAtom)
  const dashboardAnalytics = useAtomValue(dashboardAnalyticsAtom)
  const isFeatureFlagsFetched = useAtomValue(featureFlagsFetchedAtom)

  const pageInfo = useMemo(() => {
    return {
      PageFunction: 'dashboard',
      PageKind: 'multiaccount',
      previouslyVisited: `${electronicConsent}`
    }
  }, [electronicConsent])

  /*
   * Single consolidated analytics event for the dashboard workflow — fires only once.
   * Safe: DisasterAlertBanner always mounts before this component via DashboardLayout.
   */
  useEffect(() => {
    // Wait for banner component to signal its analytics contribution
    if (dashboardAnalytics.bannerResolved !== 'true') return
    if (shouldAccountDashboardSkipAnalytics(modalId)) return
    if (isAccountsLoading || isAccountsFetching) return
    if (lastAnalyticsAccountCountRef.current === numberOfAccounts) return

    const notificationPayload = dashboardAnalytics.notificationType
      ? { notificationType: dashboardAnalytics.notificationType }
      : {}
    const syfIdPayload = syfId ? { syfId } : {}

    if (isCreditAccountsAPIError) {
      lastAnalyticsAccountCountRef.current = numberOfAccounts
      setAnalyticsDefaults(pageInfo)
      triggerAnalyticsEvent({
        ...notificationPayload,
        pageName: 'error',
        MessageType: 'downtime pay message',
        ErrorMessage: 'unable to access your account',
        Message: technicalDifficultyMessage,
        PageKind: 'downtime pay',
        PageFunction: 'technical difficulty'
      })
    } else if (numberOfAccounts === 0) {
      lastAnalyticsAccountCountRef.current = numberOfAccounts
      setAnalyticsDefaults({
        ...pageInfo,
        ...syfIdPayload
      })
      triggerAnalyticsEvent({
        ...pageInfo,
        ...notificationPayload,
        ...syfIdPayload,
        pageName: 'error',
        MessageType: 'technical error',
        ErrorMessage: `${i18n({ errors: 'unableToFindAccount' })}`
      })
      // Deferred until econsent completes — WelcomeInterstitial fires its own event
    } else if (numberOfAccounts && electronicConsent) {
      lastAnalyticsAccountCountRef.current = numberOfAccounts
      setAnalyticsDefaults({
        ...pageInfo,
        numberOfAccounts: `${numberOfAccounts}`,
        ...syfIdPayload
      })
      triggerAnalyticsEvent({
        ...pageInfo,
        ...notificationPayload,
        ...syfIdPayload,
        numberOfAccounts: `${numberOfAccounts}`
      })
      // Clear PageSubFunction so it does not leak into subsequent page events
      const resetAnalytics = { PageSubFunction: '' }
      setAnalyticsDefaults(resetAnalytics)
    }
  }, [
    numberOfAccounts,
    isAccountsFetching,
    isCreditAccountsAPIError,
    dashboardAnalytics,
    modalId,
    isAccountsLoading,
    pageInfo,
    syfId,
    electronicConsent
  ])

  const isAllAccountSummaryLoading = useAllAccountSummary(accounts).some(
    (queryData) => queryData.isLoading
  )

  const isAllAccountSummaryError = useAllAccountSummary(accounts).some(
    (queryData) => queryData.isError
  )

  const isDYEnabled = isFeatureFlagEnabled('isDYEnabled')
  const isAccountsOrAutoRegisterLoading =
    isAccountsLoading || isAutoRegisterLoading
  const isAllAccountSummaryOrAutoRegisterLoading = !(
    isAllAccountSummaryLoading || isAccountsOrAutoRegisterLoading
  )
  const isCreditOrSummaryError =
    isCreditAccountsAPIError || isAllAccountSummaryError

  const lwsRedirectUri = sessionStorage.getItem('lwsRedirectUri')
  const isLwsRedirect = !!lwsRedirectUri

  let showAddAccount =
    !isCreditAccountsAPIError && !isAccountsLoading && !isLwsRedirect

  if (isFeatureFlagEnabled('isAddAccountABTestingEnabled')) {
    showAddAccount =
      isFeatureFlagsFetched &&
      showAddAccount &&
      !getBoolFromApptimize('shouldShowAddAccountInAvatar')
  }

  return (
    <PageWrapper data-test="account-dashboard">
      {renderWelcomeInterstitial && <WelcomeInterstitial />}
      <DashboardContainer vertical="base" hideOnBreakpointDown="small">
        <PersonalGreeting />
        {isAccountsOrAutoRegisterLoading ? (
          <Loader data-test="syf-loader" />
        ) : (
          <MainContainer>
            <AccountsContainer data-cy="accounts-container">
              {showAddAccount && (
                <AccountManagerContainer>
                  <AccountManagerGrid
                    id="add-account-container-id"
                    data-cy="add-account-container"
                  >
                    <AccountManagerText as="h2" weight="bold">
                      {i18n({ labels: 'yourAccounts' })}
                    </AccountManagerText>
                    <AddAccountButton />
                  </AccountManagerGrid>
                </AccountManagerContainer>
              )}
              {accountError || isAccountEmpty ? (
                <ErrorContainer>
                  <TechnicalError
                    errorType={
                      accountError ? 'technicalError' : 'noAccountsFound'
                    }
                  />
                </ErrorContainer>
              ) : (
                /*
                 * Safari removes the implicit list role when list-style: none is applied.
                 * Add role="list" to ensure Safari/VoiceOver announces it correctly
                 */
                <DashboardAccountGrid
                  className="dashboard-accounts-grid"
                  data-cy="dashboard-accounts-grid"
                  role="list"
                >
                  {accounts.map((account, index) => {
                    const { info, product } = account
                    const accountId = info['cipher.accountId']
                    return (
                      <AccountCard
                        accountId={accountId}
                        accountLast4={info?.accountLast4}
                        cardIndex={index}
                        data-test="account-card"
                        displayName={product?.displayName}
                        key={accountId}
                      />
                    )
                  })}
                </DashboardAccountGrid>
              )}
            </AccountsContainer>
            {shouldShowNotificationCenter && <NotificationCenter />}
          </MainContainer>
        )}
      </DashboardContainer>

      {isFeatureFlagEnabled('isMarketPlaceOfferEnabled') && (
        <DashboardContainer
          vertical="base"
          hideOnBreakpointDown="small"
          id="offers-modal-container"
        >
          <MarketingWrapper />
        </DashboardContainer>
      )}
      {isAllAccountSummaryOrAutoRegisterLoading &&
        isDYEnabled &&
        !isCreditOrSummaryError &&
        !isAccountEmpty && <DynamicYieldGrid />}
    </PageWrapper>
  )
}

export default AccountsDashboard
